import { customElement } from '../../core/ids-decorators';
import IdsAttachmentMixin from '../../mixins/ids-attachment-mixin/ids-attachment-mixin';
import IdsEventsMixin from '../../mixins/ids-events-mixin/ids-events-mixin';
import IdsFocusCaptureMixin from '../../mixins/ids-focus-capture-mixin/ids-focus-capture-mixin';
import IdsPopupInteractionsMixin from '../../mixins/ids-popup-interactions-mixin/ids-popup-interactions-mixin';
import IdsElement from '../../core/ids-element';
import { getClosest } from '../../utils/ids-dom-utils/ids-dom-utils';
import IdsPopup from './ids-popup';

const Base = IdsFocusCaptureMixin(
  IdsPopupInteractionsMixin(
    IdsEventsMixin(
      IdsAttachmentMixin(
        IdsElement
      )
    )
  )
);

/**
 * IDS Picker Popup Component
 * @type {IdsPickerPopup}
 * @inherits IdsElement
 * @mixes IdsAttachmentMixin
 * @mixes IdsEventsMixin
 * @mixes IdsPopupInteractionsMixin
 */

export interface IdsPickerPopupCallbacks {
  onHide?(): void;
  onShow?() : void;
}

@customElement('ids-picker-popup')
class IdsPickerPopup extends Base implements IdsPickerPopupCallbacks {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    const isFilterField = this.target?.parentElement?.classList.contains('ids-data-grid-header-cell-filter-wrapper');
    if (this.popup && (isFilterField === false || !this.popup.onOutsideClick)) {
      this.popup.onOutsideClick = this.onOutsideClick.bind(this);
    }
  }

  disconnectedCallback(): void {
    super.disconnectedCallback?.();
    if (this.popup) {
      (this.popup.onOutsideClick as any) = undefined;
    }
  }

  static get attributes(): Array<string> {
    return [...super.attributes];
  }

  /**
   * Inner template contents
   * @returns {string} The template
   */
  template(): string {
    return `<ids-popup class="ids-picker-popup" type="menu" align="bottom, left" arrow="bottom" tabIndex="-1" animated>
      <slot slot="content"></slot>
    </ids-popup>`;
  }

  /**
   * @returns {Array<string>} Date Picker vetoable events
   */
  vetoableEventTypes: Array<string> = ['beforeshow'];

  /**
   * @readonly
   * @returns {boolean} true if the inner IdsPopup is visible
   */
  get visible() {
    return this.popup?.visible || false;
  }

  onHide?(): void;

  /**
   * Hides this menu and any of its submenus.
   * @param {boolean} [doFocus] if true, instructs the listening component that focus should change to a different element
   * @returns {void}
   */
  async hide(doFocus?: boolean): Promise<void> {
    if (!this.popup?.visible) return;

    // Hide the Ids Popup and all Submenus
    this.popup.visible = false;
    await this.popup.hide();

    if (typeof this.onHide === 'function') this.onHide();
    this.popup?.removeOpenEvents();

    this.triggerEvent('hide', this, {
      bubbles: true,
      detail: {
        doFocus,
        elem: this,
      }
    });

    this.hidden = true;
  }

  onShow?(): void;

  /**
   * @returns {void}
   */
  show(): void {
    if (!this.popup || this.popup.visible) return;

    // Trigger a veto-able `beforeshow` event.
    if (!this.triggerVetoableEvent('beforeshow')) {
      return;
    }

    this.hidden = false;

    // Show the popup and do placement
    let offsetY = this.popup.classList.contains('ids-dropdown-list') ? 0 : 2;

    if (this.popup.classList.contains('ids-date-picker-popup') || this.popup.classList.contains('ids-time-picker-popup')) {
      if (this.target === null && this.triggerElem !== null) {
        if (this.triggerElem.nodeName === 'IDS-TRIGGER-BUTTON') {
          this.target = this.triggerElem.parentElement;
        } else {
          this.target = this.triggerElem;
        }
      }
      const isDataGrid = this.getAttribute('attachment') === '.ids-data-grid-wrapper';
      const isFilterRow = isDataGrid && this.hasAttribute('data-filter-type');
      const rowHeight = isDataGrid ? (this.getRootNode() as any).host?.getAttribute('row-height') : '';

      const isCompact = getClosest(this.popup, '.compact');
      const isFilterField = getClosest(this, 'ids-date-picker')?.parentElement?.nodeName === 'IDS-FILTER-FIELD'
        || getClosest(this, 'ids-time-picker')?.parentElement?.nodeName === 'IDS-FILTER-FIELD';

      if (isFilterField) offsetY = 0;
      if (isDataGrid) {
        offsetY = rowHeight === 'xs' || rowHeight === 'xxs' ? -2 : -5;

        if (isFilterRow) {
          offsetY = 3;
        }
      }
      if (isCompact) offsetY = isFilterField ? 0 : -11;

      this.popup.onPlaceEnd = (popupRect: any, popup: IdsPopup) => {
        // Workaround for the arrow position
        if (popup?.arrowEl) {
          let arrowOffset = parseInt(popup?.arrowEl?.style.marginLeft);
          if (isFilterRow) arrowOffset = parseInt(popup?.arrowEl?.style.marginLeft);
          popup.arrowEl.style.marginLeft = `${arrowOffset}px`;
          requestAnimationFrame(() => {
            popup.placeArrow();
          });
        }

        if (popup?.container?.classList.contains('flipped')) {
          let flippedOffset = (this.popup?.classList.contains('ids-date-picker-popup') ? 1 : 9);
          if (isDataGrid) {
            flippedOffset = (this.popup?.classList.contains('ids-date-picker-popup') ? 8 : 9);
            if (rowHeight === 'xs' || rowHeight === 'xxs') flippedOffset = (this.popup?.classList.contains('ids-date-picker-popup') ? 5 : 6);
          }

          popup.style.top = `${parseInt(popup.style.top) + flippedOffset}px`;
        }
        return popupRect;
      };
    }

    this.popup.y = offsetY;

    this.popup.visible = true;

    if (typeof this.onShow === 'function') this.onShow();

    this.popup?.addOpenEvents();

    if (this.popup.classList.contains('ids-date-picker-popup') || this.popup.classList.contains('ids-time-picker-popup')) {
      if (this.popup && this.popup.visible) {
        this.popup.place(true);
      }
    }

    this.triggerEvent('show', this, {
      bubbles: true,
      detail: {
        elem: this
      }
    });
  }

  /**
   * Toggles visibility of the popup on/off depending on its current state
   * @returns {void}
   */
  toggleVisibility() {
    if (!this.popup) return;
    if (!this.popup.visible) {
      this.show();
    } else {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.hide();
    }
  }

  onOutsideClick(e: Event) {
    const target = (e.target as HTMLElement);
    if (!this.contains(target)) {
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.hide();
    }
  }

  /**
   * Inherited from `IdsPopupInteractionsMixin`
   */
  onTriggerClick() {
    // Partly Managed now in date/time picker in attachEvents since this may not be created
    if (this.parentElement?.classList.contains('ids-data-grid-wrapper')) this.toggleVisibility();
    if (this.nodeName === 'IDS-TIME-PICKER-POPUP' || this.nodeName === 'IDS-DATE-PICKER-POPUP') return;
    this.toggleVisibility();
  }

  /**
   * Inherited from the Popup Interactions Mixin.
   * Runs as soon as the Popup is connected to the DOM.
   */
  onTriggerImmediate(): void {
    this.show();
  }
}

export default IdsPickerPopup;
